/**
 * セクションカード: 編集 / AI 再生成 / AI 壁打ち / 削除 / コピー
 */
import type { MailSection } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import {
  Check,
  Copy,
  Edit3,
  Lightbulb,
  Link2,
  MousePointerClick,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { BrainstormDialog, type BrainstormResult } from './BrainstormDialog';

import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { functions } from '@/lib/firebase';

interface MailContext {
  currentSubject: string;
  sections: MailSection[];
  overview?: string;
  lang?: string;
}

const regenerateSectionFn = httpsCallable<
  { section: MailSection; context: MailContext; instruction?: string },
  { ok: boolean; content: string; label: string; reasoning: string; modelUsed: string }
>(functions, 'regenerateSection');

const brainstormSectionFn = httpsCallable<
  { section: MailSection; context: MailContext; userFeedback: string },
  BrainstormResult & { ok: boolean; modelUsed: string }
>(functions, 'brainstormSection');

interface SectionCardProps {
  section: MailSection;
  context: MailContext;
  onChange: (next: MailSection) => void;
  onDelete: () => void;
  /**
   * 編集中（未確定）の本文を親へ伝える。編集を終えたら null。
   * 親の保存ボタンが未確定の入力を自動で確定するために使う（spec_mail_system.md B-19.3）。
   */
  onDraftChange?: (draft: string | null) => void;
}

export function SectionCard({
  section,
  context,
  onChange,
  onDelete,
  onDraftChange,
}: SectionCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.content);
  const [regenerating, setRegenerating] = useState(false);
  const [brainstormOpen, setBrainstormOpen] = useState(false);

  const startEdit = () => {
    setDraft(section.content);
    setEditing(true);
    onDraftChange?.(section.content);
  };

  const changeDraft = (next: string) => {
    setDraft(next);
    onDraftChange?.(next);
  };

  const commitEdit = () => {
    onChange({ ...section, content: draft });
    setEditing(false);
    onDraftChange?.(null);
  };

  const cancelEdit = () => {
    setDraft(section.content);
    setEditing(false);
    onDraftChange?.(null);
  };

  // 編集中のままカードが消えた（削除・表示切替・クリア等）場合は未確定の入力を破棄する
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => () => onDraftChangeRef.current?.(null), []);

  // 編集窓の外をクリックしたら ✅ と同じく「確定」扱いにする（UX: ✅ 押し忘れ防止）。
  // クリックアウェイ方式: カード内（✅/✕ ボタン含む）をクリックした場合は確定しない
  //  → ✕ の取消や ✅ の確定はそれぞれのボタンハンドラに任せる。
  // onBlur 方式はブラウザによりボタンクリック時に relatedTarget が取れず誤確定するため不採用。
  const cardRef = useRef<HTMLDivElement>(null);
  const commitEditRef = useRef(commitEdit);
  commitEditRef.current = commitEdit;
  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = cardRef.current;
      if (el && !el.contains(e.target as Node)) {
        commitEditRef.current();
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [editing]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await regenerateSectionFn({ section, context });
      onChange({ ...section, content: res.data.content, label: res.data.label });
      toast.success(res.data.reasoning || 'セクションを再生成しました');
    } catch (err) {
      toast.error(`再生成失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(section.content);
      toast.success('セクション本文をコピーしました');
    } catch {
      toast.error('コピーに失敗');
    }
  };

  const handleDelete = () => {
    if (!window.confirm(`セクション「${section.label}」を削除します。`)) return;
    onDelete();
  };

  const runBrainstorm = async (userFeedback: string): Promise<BrainstormResult> => {
    try {
      const res = await brainstormSectionFn({ section, context, userFeedback });
      return {
        proposal: res.data.proposal,
        alternatives: res.data.alternatives,
        reasoning: res.data.reasoning,
      };
    } catch (err) {
      toast.error(`壁打ち失敗: ${err instanceof Error ? err.message : String(err)}`);
      throw err;
    }
  };

  return (
    <div ref={cardRef} className="rounded bg-[var(--bg-elevated)] border border-[var(--border)]">
      <div className="flex items-center justify-between p-2 bg-[var(--bg-hover)] border-b border-[var(--border)] rounded-t text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{section.label}</span>
          <code className="text-[10px] font-mono text-[var(--text-muted)]">{section.type}</code>
        </div>
        <div className="flex gap-0.5">
          {!editing && (
            <>
              <Button type="button" variant="ghost" size="icon" onClick={handleCopy} title="コピー">
                <Copy className="size-3" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={startEdit} title="編集">
                <Edit3 className="size-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleRegenerate}
                disabled={regenerating}
                title="AI 再生成"
              >
                {regenerating ? <Spinner className="size-3" /> : <RefreshCw className="size-3" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setBrainstormOpen(true)}
                title="AI 壁打ち"
              >
                <Lightbulb className="size-3" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={handleDelete} title="削除">
                <Trash2 className="size-3" />
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button type="button" variant="ghost" size="icon" onClick={commitEdit} title="確定">
                <Check className="size-3" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={cancelEdit} title="取消">
                <X className="size-3" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="p-3 text-[13px] whitespace-pre-wrap leading-relaxed">
        {editing ? (
          <Textarea
            value={draft}
            onChange={(e) => changeDraft(e.target.value)}
            rows={Math.max(4, draft.split('\n').length + 1)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancelEdit();
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') commitEdit();
            }}
          />
        ) : (
          section.content
        )}
      </div>
      {section.type === 'cta' && (
        <div className="p-3 border-t border-[var(--border)] space-y-2">
          <div className="flex items-center gap-2">
            <MousePointerClick className="size-4 text-[var(--accent)] flex-shrink-0" />
            <Input
              type="text"
              placeholder="ボタンラベル（例: 詳細を見る）"
              value={section.cta_button_label ?? ''}
              onChange={(e) => onChange({ ...section, cta_button_label: e.target.value })}
              className="flex-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <Link2 className="size-4 text-[var(--accent)] flex-shrink-0" />
            <Input
              type="url"
              placeholder="https://... （CTA リンク URL）"
              value={section.cta_url_placeholder ?? ''}
              onChange={(e) => onChange({ ...section, cta_url_placeholder: e.target.value })}
              className="flex-1"
            />
          </div>
          {section.cta_position_note && (
            <div className="flex items-start gap-2 text-xs text-[var(--text-muted)]">
              <span className="size-1 rounded-full bg-[var(--text-muted)] mt-2 flex-shrink-0" />
              <span>{section.cta_position_note}</span>
            </div>
          )}
        </div>
      )}

      <BrainstormDialog
        open={brainstormOpen}
        onOpenChange={setBrainstormOpen}
        title={`「${section.label}」セクションの壁打ち`}
        currentContent={section.content}
        runBrainstorm={runBrainstorm}
        onAdopt={(c) => {
          onChange({ ...section, content: c });
          toast.success('セクションを更新しました');
        }}
      />
    </div>
  );
}
